'use strict';

Object.defineProperty(exports, Symbol.toStringTag, { value: 'Module' });

const react = require('@emotion/react');
const styleEncapsulation = require('@zapier/style-encapsulation');
const theme_Colors = require('../../../../theme/Colors.cjs.js');
require('../../../../theme/Shadows.cjs.js');
require('../../../../theme/Typography.cjs.js');
const theme_LegacyTypography = require('../../../../theme/LegacyTypography.cjs.js');
require('react');
const theme_LegacyColorMap = require('../../../../theme/LegacyColorMap.cjs.js');
const utils_createUniqueCustomProperty = require('../../../../utils/createUniqueCustomProperty.cjs.js');
const components_display_Heading_Heading = require('../../../display/Heading/Heading.cjs.js');
require('../../../../utils/heading-level-context/HeadingLevelContext.cjs.js');
const components_display_Icon_Icon = require('../../../display/Icon/Icon.cjs.js');
const components_display_Tag_Tag = require('../../../display/Tag/Tag.cjs.js');
const components_display_Text_Text = require('../../../display/Text/Text.cjs.js');
require('../../SubscriptionForm/SubscriptionForm.cjs.js');
const components_marketing_SubscriptionForm_SubscriptionFormWithDisclaimer = require('../../SubscriptionForm/SubscriptionFormWithDisclaimer.cjs.js');
require('../../SubscriptionForm/DisclaimerText.cjs.js');
const components_layout_Grid_Grid = require('../../../layout/Grid/Grid.cjs.js');
const components_navigation_Button_Button = require('../../../navigation/Button/Button.cjs.js');
const components_marketing_Hero_constants = require('../constants.cjs.js');
const components_marketing_Hero_utils = require('../utils.cjs.js');
const styleMixins_reset = require('../../../../style-mixins/reset.cjs.js');

const Vars = utils_createUniqueCustomProperty.createUniqueCustomProperties("HeroColorBlock", [
  "primaryBackgroundColorStop",
  "secondaryBackgroundColorStop"
]);
const SubscriptionFormStyles = {
  ctaContainer: react.css`
    ${styleMixins_reset.boxSizing};
    max-width: 470px;
    @media screen and (min-width: ${components_marketing_Hero_constants.breakpoints.tablet}px) {
      align-items: stretch;
      flex-direction: column;
    }
  `,
  ctaDisclaimer: react.css`
    ${styleMixins_reset.boxSizing};
    margin: 10px 0;
    text-align: left;
  `
};
const Styles = styleEncapsulation.labelStyles("HeroColorBlock", {
  root: (backgroundColor = "zapier") => react.css`
    ${styleMixins_reset.boxSizing};
    padding: 20px 19.5px;
    ${Vars.primaryBackgroundColorStop}: ${theme_Colors.Colors[theme_LegacyColorMap.LegacyColorMap[backgroundColor]]};
    ${Vars.secondaryBackgroundColorStop}: ${theme_Colors.Colors.PrimeWhite};
    background: linear-gradient(
      to bottom,
      var(${Vars.primaryBackgroundColorStop}) 80%,
      var(${Vars.secondaryBackgroundColorStop}) 20%
    );
    @media screen and (min-width: ${components_marketing_Hero_constants.breakpoints.mobile}px) {
      padding: 40px 31px;
      background: linear-gradient(
        to bottom,
        var(${Vars.primaryBackgroundColorStop}) 75%,
        var(${Vars.secondaryBackgroundColorStop}) 25%
      );
    }
    @media screen and (min-width: ${components_marketing_Hero_constants.breakpoints.tablet}px) {
      padding: 60px 31px;
      background: linear-gradient(
        to bottom,
        var(${Vars.primaryBackgroundColorStop}) 54.55%,
        var(${Vars.secondaryBackgroundColorStop}) 45.45%
      );
    }
  `,
  content: react.css`
    ${styleMixins_reset.boxSizing};
    max-width: 770px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
  `,
  textContent: react.css`
    ${styleMixins_reset.boxSizing};
    margin: auto;
  `,
  labelContainer: react.css`
    ${styleMixins_reset.boxSizing};
    margin-bottom: 10px;
  `,
  heading: (backgroundColor) => react.css`
    ${styleMixins_reset.boxSizing};
    ${theme_LegacyTypography.LegacyTypography.pageHeader4}
    color: ${theme_Colors.Colors[backgroundColor === "zapier" ? "BrandCream" : "PrimeWhite"]};
    margin-bottom: 10px;
    text-align: center;

    @media screen and (min-width: ${components_marketing_Hero_constants.breakpoints.tablet}px) {
      ${theme_LegacyTypography.LegacyTypography.pageHeader3}
      margin-bottom: 20px;
    }
  `,
  description: (backgroundColor) => react.css`
    ${styleMixins_reset.boxSizing};
    ${theme_LegacyTypography.LegacyTypography.pageHeader9}
    color: ${theme_Colors.Colors[backgroundColor === "zapier" ? "BrandCream" : "PrimeWhite"]};
    text-align: center;
    margin: 0 auto;
    max-width: 570px;
  `,
  ctaContainer: (isSubscriptionForm = false) => react.css`
    ${styleMixins_reset.boxSizing};
    margin-top: 20px;
    text-align: center;
    width: 100%;
    @media screen and (min-width: ${components_marketing_Hero_constants.breakpoints.tablet}px) {
      margin-top: 40px;
    }
    ${isSubscriptionForm && styleEncapsulation.withModifier("subscription-form", SubscriptionFormStyles.ctaContainer)}
  `,
  ctaButtonWrapper: react.css`
    ${styleMixins_reset.boxSizing};
    margin: 0 auto;

    // Button should take up full width in mobile only;
    button,
    a {
      width: 100%;
    }

    @media screen and (min-width: ${components_marketing_Hero_constants.breakpoints.tablet}px) {
      button,
      a {
        width: auto;
      }
    }
  `,
  ctaDisclaimer: (isSubscriptionForm = false) => react.css`
    ${styleMixins_reset.boxSizing};
    margin: 10px 0 0 0;
    text-align: center;
    ${isSubscriptionForm && styleEncapsulation.withModifier("subscription-form-disclaimer", SubscriptionFormStyles.ctaDisclaimer)}
  `,
  mediaContent: react.css`
    ${styleMixins_reset.boxSizing};
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 40px;
  `
});
const HeroColorBlock = ({ ctaColor = "primary-alt", isSubscriptionForm = false, subscriptionPlaceholder = "Email address", backgroundColor = "zapier", heading, description, ctaText, ctaHref, ctaTarget, ctaDisabled = false, ctaDisclaimer, onCtaClick, onCtaKeyDown, children, onSubscriptionFormSubmit, label, labelIcon, labelColor }) => {
  const mappedBackgroundColor = components_marketing_Hero_utils.transformBackgroundColor(backgroundColor);
  components_marketing_Hero_utils.validateZapierBackgroundAccessibility(mappedBackgroundColor, isSubscriptionForm, !!ctaDisclaimer);
  const getCtaComponent = () => {
    const sharedProps = {
      children: ctaText,
      href: !isSubscriptionForm ? ctaHref : void 0,
      onClick: onCtaClick,
      onKeyDown: onCtaKeyDown,
      size: isSubscriptionForm ? "medium" : "large",
      type: isSubscriptionForm ? "submit" : "button",
      "data-testid": "color-block-hero-cta-button",
      target: ctaTarget,
      disabled: ctaDisabled
    };
    const color = components_marketing_Hero_utils.getSupportedCTAColor(ctaColor, mappedBackgroundColor);
    return react.jsx(components_navigation_Button_Button.Button, Object.assign({}, sharedProps, {
      // "primary-alt" no longer exists as of V8 and is now mapped to "primary"
      color: color === "primary-alt" ? "primary" : color
    }));
  };
  return react.jsx(
    "div",
    { css: Styles.root(mappedBackgroundColor), "data-background-color": mappedBackgroundColor, "data-zds": true },
    react.jsx(
      "div",
      { css: Styles.content, "data-zds": true },
      label && react.jsx(
        "div",
        { css: Styles.labelContainer, "data-zds": true },
        react.jsx(components_display_Tag_Tag.Tag, { variant: labelColor }, labelIcon ? react.jsx(
          components_layout_Grid_Grid.Grid,
          { alignItems: "center", columns: "auto max-content", gap: 5 },
          react.jsx(components_display_Icon_Icon.Icon, { name: labelIcon, size: 20 }),
          label
        ) : label)
      ),
      react.jsx(components_display_Heading_Heading.Heading, { css: Styles.heading(mappedBackgroundColor) }, heading),
      description && react.jsx("div", { css: Styles.description(mappedBackgroundColor), "data-zds": true }, description),
      ctaText && (ctaHref || onCtaClick || isSubscriptionForm) && react.jsx("div", { css: Styles.ctaContainer(isSubscriptionForm), "data-zds": true }, isSubscriptionForm ? react.jsx(components_marketing_SubscriptionForm_SubscriptionFormWithDisclaimer.SubscriptionFormWithDisclaimer, { placeholderText: subscriptionPlaceholder, onSubmit: onSubscriptionFormSubmit, renderButton: () => getCtaComponent(), disclaimerTextColor: mappedBackgroundColor === "neutral100" ? "primary" : "secondary" }) : react.jsx(
        "div",
        { "data-zds": true },
        react.jsx("div", { css: Styles.ctaButtonWrapper, "data-zds": true }, getCtaComponent()),
        ctaDisclaimer && react.jsx(
          "div",
          { css: Styles.ctaDisclaimer(isSubscriptionForm), "data-zds": true },
          react.jsx(components_display_Text_Text.Text, { tag: "div", type: "Body1", color: mappedBackgroundColor === "zapier" ? "BrandCream" : "PrimeWhite" }, ctaDisclaimer)
        )
      )),
      react.jsx("div", { css: Styles.mediaContent, "data-zds": true }, children)
    )
  );
};

exports.HeroColorBlock = HeroColorBlock;
//# sourceMappingURL=HeroColorBlock.cjs.js.map
